/* eslint-disable no-case-declarations */

import { FormStateMap, reducer as formReducer } from 'redux-form';

import { AllActions } from '../actions/allActions';
import { RequestLoadingErrorAction, RequestSuccessfulAction } from '../actions/errorActions';
import { AppForm } from '../components/form/appForm';
import { AppPartialSubmitForm } from '../components/form/appPartialSubmitForm';
import { InternalActionTypes } from '../constants/actionTypes';
import { FormId } from '../constants/formId';

function clearEmptyErrors(errors: any) {
  if (!errors) {
    return undefined;
  }

  switch (typeof errors) {
    case 'string':
      return errors === '' ? undefined : errors;
    case 'object':
      if (Array.isArray(errors)) {
        const clearedArray: any = errors.map(clearEmptyErrors);
        const notUndefinedContents = clearedArray.find((i: any) => !!i);

        return notUndefinedContents ? clearedArray : undefined;
      }
      const clearedObject: any = Object.keys(errors).reduce((result, k) => {
        const clearedValue = clearEmptyErrors(errors[k]);

        if (clearedValue) {
          return {
            ...result,
            [k]: clearedValue,
          };
        }

        return result;
      }, {});

      return Object.keys(clearedObject).length === 0 ? undefined : clearedObject;

    default:
      return errors;
  }
}

function handleRequestFailures(state: FormStateMap, action: RequestLoadingErrorAction): FormStateMap {
  if (action.component instanceof AppPartialSubmitForm) {
    return {
      ...state,
      [action.component.props.form]: {
        ...state[action.component.props.form],
        submitErrors: {
          _message: action.error.message,
          ...action.error.errors,
        },
        submitFailed: true,
        submitting: false,
        values: {
          ...action.component.props.initialValues,
          ...state[action.component.props.form].values,
        },
      },
    };
  }

  if (action.component instanceof AppForm) {
    return {
      ...state,
      [action.component.props.form]: {
        ...state[action.component.props.form],
        submitErrors: {
          _message: action.error.message,
          ...action.error.errors,
        },
        submitFailed: true,
        submitting: false,
      },
    };
  }

  return state;
}

function handleRequestSuccess(state: FormStateMap, action: RequestSuccessfulAction): FormStateMap {
  if (action.component instanceof AppPartialSubmitForm) {
    return {
      ...state,
      [action.component.props.form]: {
        ...state[action.component.props.form],
        submitFailed: false,
        submitting: false,
        values: {
          ...action.component.props.initialValues,
          ...state[action.component.props.form].values,
        },
      },
    };
  }

  if (action.component instanceof AppForm) {
    return {
      ...state,
      [action.component.props.form]: {
        ...state[action.component.props.form],
        submitFailed: false,
        submitting: false,
      },
    };
  }

  return state;
}

export function customFormReducer(state: FormStateMap, action: AllActions): FormStateMap {
  const updatedState = formReducer(state, action);

  switch (action.type) {
    case InternalActionTypes.RequestLoadingError:
      return handleRequestFailures(updatedState, action);
    case InternalActionTypes.RequestSuccessful:
      return handleRequestSuccess(updatedState, action);
    case InternalActionTypes.ClearSyncErrors:
      const clearSyncForm = updatedState[action.formId];

      if (clearSyncForm) {
        return {
          ...updatedState,
          [action.formId]: { ...clearSyncForm, syncErrors: clearEmptyErrors((clearSyncForm as any).syncErrors) },
        } as any;
      }

      return updatedState;

    case InternalActionTypes.UserCreated:
      const createUserFormState = updatedState[FormId.CreateUserForm];
      createUserFormState.values.password = action.result.entities.users[action.result.result.toLowerCase()].password;

      return {
        ...updatedState,
        [FormId.CreateUserForm]: { ...createUserFormState },
      };
    case InternalActionTypes.UpdateFormData:
      const formState = updatedState[action.formId];

      if (formState.values) {
        formState.values = { ...formState.values, ...action.formData };
      }

      return {
        ...updatedState,
        [action.formId]: { ...formState },
      };
    default:
      return updatedState;
  }
}
